import api from "../api";

export const PUSH_SUPPORTED =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
};

export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!PUSH_SUPPORTED) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration("/");
    if (existing) return existing;
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (err) {
    console.error("[push] service worker registration failed", err);
    return null;
  }
};

export const getNotificationPermission = (): NotificationPermission =>
  PUSH_SUPPORTED ? Notification.permission : "denied";

export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!PUSH_SUPPORTED) return "denied";
  return Notification.requestPermission();
};

export const subscribeToPush = async (): Promise<PushSubscription | null> => {
  if (!PUSH_SUPPORTED) return null;

  const registration = await registerServiceWorker();
  if (!registration) return null;

  const { data } = await api.get<{ publicKey: string }>("/push/vapid-key");
  if (!data?.publicKey) {
    console.warn("[push] server has no VAPID public key configured");
    return null;
  }

  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    await api.post("/push/subscribe", existing.toJSON());
    return existing;
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.publicKey),
  });

  await api.post("/push/subscribe", subscription.toJSON());
  return subscription;
};

// Fan-side (Phase 3c): the tracking page's 🔔. Same browser subscription as
// the talent flow (one per origin), posted to the order-scoped endpoint —
// "ping me about THIS request" — instead of the talent store. Returns true
// only when the subscription is registered server-side.
export const subscribeFanPush = async (orderId: string): Promise<boolean> => {
  if (!PUSH_SUPPORTED) return false;
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;
  const registration = await registerServiceWorker();
  if (!registration) return false;

  const { data } = await api.get<{ publicKey: string }>("/push/vapid-key");
  if (!data?.publicKey) return false;

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    }));

  await api.post(`/orders/${orderId}/push-subscribe`, {
    subscription: subscription.toJSON(),
  });
  return true;
};

export const unsubscribeFromPush = async (): Promise<void> => {
  if (!PUSH_SUPPORTED) return;
  const registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  try {
    await api.post("/push/unsubscribe", { endpoint: subscription.endpoint });
  } catch (err) {
    console.warn("[push] failed to notify server of unsubscribe", err);
  }
  await subscription.unsubscribe();
};

export const pingDesktop = async (): Promise<void> => {
  try {
    await api.post("/push/ping", {});
  } catch {
    // silent — heartbeat failure is not user-visible
  }
};

export const sendInstallEmail = async (): Promise<void> => {
  await api.post("/push/send-install-email", {});
};
